"use client"

import { useMemo, useState } from "react"
import { usePathname } from "next/navigation"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  KeyboardShortcut,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  Sidebar as SidebarUI,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useSidebar,
} from "@ui/shad"
import { Bell, CalendarDays, Home, Moon, Search, Sun } from "lucide-react"
import { useTheme } from "next-themes"

import { ALL_BOOKS_DISABLED_TOOLTIP, isAllBooksId } from "@/lib/all-books"
import { useFetchFeatureFlags } from "@/hooks/api/feature-flags/queries"
import { useFetchCurrentUser } from "@/hooks/api/users"
import { useBookId } from "@/hooks/use-book-id"
import { Link } from "@/components/book-link"
import { CommandPalette } from "@/components/dialogs/command-palette"
import { BookSwitcher } from "@/components/layout/book-switcher"
import { SidebarSettings } from "@/components/layout/sidebar-settings"
import NotificationMenu from "@/components/notifications/notification-menu"

import { sidebarGroups, SidebarRoute, SidebarSubRoute } from "./sidebarGroups"

export function Sidebar() {
  const { data: user } = useFetchCurrentUser()
  const { data: featureFlags } = useFetchFeatureFlags()
  const pathname = usePathname()
  const bookId = useBookId()
  const { theme, setTheme } = useTheme()
  const { state: sidebarState } = useSidebar()
  const { openDialog } = useGlobalDialogStore()
  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false)

  const currentBookId = bookId ?? pathname.split("/")[1]
  const inAllBooksMode = isAllBooksId(currentBookId)

  const isRouteActive = (routeHref: string, exact?: boolean) => {
    const pathWithoutBookId = pathname.split("/").slice(2).join("/")
    const normalizedPath = "/" + pathWithoutBookId

    return exact ? normalizedPath === routeHref : normalizedPath.startsWith(routeHref)
  }

  const isSubRouteExactMatch = (subRoutes: { href: string }[]) => {
    const pathWithoutBookId = pathname.split("/").slice(2).join("/")
    const normalizedPath = "/" + pathWithoutBookId

    return subRoutes.some((subRoute) => normalizedPath === subRoute.href)
  }

  const universalActions = [
    {
      href: "/dashboard",
      icon: Home,
      label: "Home",
      show: true,
      exact: true,
      supportsAllBooks: true,
    },
    // Notifications handled separately to open popover instead of navigation
    {
      href: "/notifications",
      icon: Bell,
      label: "Notifications",
      show: true,
      exact: true,
      supportsAllBooks: true,
    },
    {
      href: "/calendar",
      icon: CalendarDays,
      label: "Calendar",
      show: true,
      exact: false,
      supportsAllBooks: true,
    },
  ]

  const showBookSwitcher = useMemo(() => {
    if (!user) return false
    return user.books.length > 1
  }, [user])

  const filterFeatureFlag = (route: SidebarRoute | SidebarSubRoute) => {
    if (route.featureFlag === "internal") {
      return (
        user?.email?.includes("pageport.co") ||
        user?.email?.includes("pageport.com") ||
        user?.email?.includes("example.com")
      )
    }
    if (route.featureFlag === "beta" || !route.featureFlag) return true

    return featureFlags?.includes(route.featureFlag) ?? false
  }

  const visibleSidebarGroups = sidebarGroups.filter((group) => group.routes.some(filterFeatureFlag))

  return (
    <div className="flex h-screen">
      {showBookSwitcher && <BookSwitcher collapsed={sidebarState === "collapsed"} />}
      <SidebarUI collapsible="icon" offset={showBookSwitcher}>
        <SidebarHeader className="bg-sidebar group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-2">
          <SidebarSettings />
          <button
            onClick={() => {
              openDialog({
                variant: "custom",
                content: <CommandPalette />,
                dialogContentClasses: "max-w-[840px] h-[420px]",
              })
            }}
            className="border-input bg-sidebar ring-offset-background hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-sidebar-ring relative flex h-9 w-full items-center overflow-hidden rounded-md border px-3 py-2 text-sm whitespace-nowrap transition-colors group-data-[collapsible=icon]:my-0.5 group-data-[collapsible=icon]:mr-0 group-data-[collapsible=icon]:ml-2 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:border-0 group-data-[collapsible=icon]:p-2! focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            title="Search anything"
          >
            <Search
              strokeWidth={1.5}
              className="text-sidebar-icon mr-2 h-4 w-4 shrink-0 group-data-[collapsible=icon]:mr-0"
            />
            <span className="text-sidebar-foreground-muted min-w-0 flex-1 truncate text-left group-data-[collapsible=icon]:hidden">
              Search anything
            </span>
            <span className="shrink-0 group-data-[collapsible=icon]:hidden">
              <KeyboardShortcut keys={["⌘", "K"]} />
            </span>
          </button>
        </SidebarHeader>
        <SidebarContent className="bg-sidebar gap-0">
          <SidebarGroup key="universal-actions" className="pt-1 group-data-[collapsible=icon]:pb-0!">
            <SidebarMenu>
              {universalActions
                .filter((route) => route.show)
                .map((route) => {
                  const isUniversalActionDisabled = inAllBooksMode && !route.supportsAllBooks

                  if (route.href.includes("/notifications")) {
                    return isUniversalActionDisabled ? (
                      <TooltipProvider key={route.href + route.label}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <SidebarMenuButton asChild>
                              <span className="cursor-not-allowed opacity-50">
                                <route.icon strokeWidth={1.5} size={16} className="text-sidebar-icon" />
                                <span className="flex-1 group-data-[collapsible=icon]:hidden">{route.label}</span>
                              </span>
                            </SidebarMenuButton>
                          </TooltipTrigger>
                          <TooltipContent>{ALL_BOOKS_DISABLED_TOOLTIP}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <SidebarMenuButton
                        key={route.href + route.label}
                        asChild
                        isActive={isRouteActive(route.href, route.exact)}
                        tooltip={route.label}
                        tooltipOpen={notificationMenuOpen ? false : undefined}
                        className="has-[:focus-visible]:ring-sidebar-ring has-[:focus-visible]:ring-2"
                      >
                        <div>
                          <NotificationMenu
                            onOpenChange={setNotificationMenuOpen}
                            trigger={(count) => (
                              <button
                                type="button"
                                aria-label={route.label}
                                className="flex w-full min-w-0 cursor-pointer items-center gap-2 overflow-hidden text-left whitespace-nowrap outline-none"
                              >
                                <route.icon
                                  strokeWidth={isRouteActive(route.href, route.exact) ? 2 : 1.5}
                                  size={16}
                                  className="text-sidebar-icon shrink-0"
                                />
                                <span className="min-w-0 flex-1 truncate group-data-[collapsible=icon]:hidden">
                                  {route.label}
                                </span>
                                {count > 0 && (
                                  <Badge
                                    variant="default"
                                    size="xs"
                                    className="ml-auto shrink-0 px-1.5 group-data-[collapsible=icon]:hidden"
                                  >
                                    {count > 99 ? "99+" : count}
                                  </Badge>
                                )}
                              </button>
                            )}
                          />
                        </div>
                      </SidebarMenuButton>
                    )
                  }

                  return isUniversalActionDisabled ? (
                    <TooltipProvider key={route.href + route.label}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton asChild>
                            <span className="cursor-not-allowed opacity-50">
                              <route.icon strokeWidth={1.5} size={16} className="text-sidebar-icon" />
                              <span className="group-data-[collapsible=icon]:hidden">{route.label}</span>
                            </span>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        <TooltipContent>{ALL_BOOKS_DISABLED_TOOLTIP}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <SidebarMenuButton
                      key={route.href + route.label}
                      asChild
                      isActive={isRouteActive(route.href, route.exact)}
                      tooltip={route.label}
                    >
                      <Link href={route.href}>
                        <route.icon
                          strokeWidth={isRouteActive(route.href, route.exact) ? 2 : 1.5}
                          size={16}
                          className="text-sidebar-icon"
                        />
                        <span>{route.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  )
                })}
            </SidebarMenu>
          </SidebarGroup>
          {visibleSidebarGroups.map((group) => (
            <SidebarGroup key={group.groupLabel} className="group-data-[collapsible=icon]:py-0!">
              <SidebarGroupLabel className="group-data-[collapsible=icon]:mt-0 group-data-[collapsible=icon]:h-4 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:opacity-100">
                <span className="group-data-[collapsible=icon]:sr-only">{group.groupLabel}</span>
                <span
                  aria-hidden="true"
                  className="bg-sidebar-border hidden h-px w-full group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:block group-data-[collapsible=icon]:w-[31px] group-data-[collapsible=icon]:flex-none"
                />
              </SidebarGroupLabel>
              <SidebarMenu>
                {group.routes
                  .filter((route) => filterFeatureFlag(route as SidebarRoute))
                  .map((route) => {
                    const visibleSubRoutes = route.subRoutes?.filter(filterFeatureFlag)
                    const allBooksSubRoute = visibleSubRoutes?.find((subRoute) => subRoute.supportsAllBooks)
                    const parentHrefInAllBooks =
                      visibleSubRoutes?.find((subRoute) => subRoute.href === route.href && subRoute.supportsAllBooks)
                        ?.href ?? allBooksSubRoute?.href
                    const parentHref = inAllBooksMode && parentHrefInAllBooks ? parentHrefInAllBooks : route.href
                    const isRouteDisabled = inAllBooksMode && !route.supportsAllBooks && !allBooksSubRoute

                    return visibleSubRoutes ? (
                      <Collapsible key={route.href + route.label} asChild open={isSubRouteExactMatch(visibleSubRoutes)}>
                        <SidebarMenuItem>
                          {isRouteDisabled ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SidebarMenuButton asChild>
                                    <span className="cursor-not-allowed opacity-50">
                                      <route.icon
                                        strokeWidth={1.5}
                                        width={16}
                                        height={16}
                                        className="text-sidebar-icon"
                                      />
                                      <span className="group-data-[collapsible=icon]:hidden">{route.label}</span>
                                    </span>
                                  </SidebarMenuButton>
                                </TooltipTrigger>
                                <TooltipContent>{ALL_BOOKS_DISABLED_TOOLTIP}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <SidebarMenuButton asChild isActive={isRouteActive(route.href)} tooltip={route.label}>
                              <Link href={parentHref}>
                                <route.icon
                                  strokeWidth={isRouteActive(route.href) ? 2 : 1.5}
                                  width={16}
                                  height={16}
                                  className="text-sidebar-icon"
                                />
                                <span>{route.label}</span>
                              </Link>
                            </SidebarMenuButton>
                          )}
                          <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden duration-150">
                            <SidebarMenuSub>
                              {visibleSubRoutes.map((subRoute) => {
                                const isDisabled = inAllBooksMode && !subRoute.supportsAllBooks
                                return (
                                  <SidebarMenuSubItem key={subRoute.href}>
                                    {isDisabled ? (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <SidebarMenuSubButton asChild>
                                              <span className="cursor-not-allowed opacity-50">{subRoute.label}</span>
                                            </SidebarMenuSubButton>
                                          </TooltipTrigger>
                                          <TooltipContent>{ALL_BOOKS_DISABLED_TOOLTIP}</TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    ) : (
                                      <SidebarMenuSubButton asChild isActive={isRouteActive(subRoute.href, true)}>
                                        <Link href={subRoute.href}>{subRoute.label}</Link>
                                      </SidebarMenuSubButton>
                                    )}
                                  </SidebarMenuSubItem>
                                )
                              })}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    ) : isRouteDisabled ? (
                      <TooltipProvider key={route.href + route.label}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <SidebarMenuButton asChild>
                              <span className="cursor-not-allowed opacity-50">
                                <route.icon strokeWidth={1.5} width={16} height={16} className="text-sidebar-icon" />
                                <span className="group-data-[collapsible=icon]:hidden">{route.label}</span>
                                {route.badge && (
                                  <Badge
                                    variant="brand"
                                    size="xs"
                                    className="ml-auto px-1.5 group-data-[collapsible=icon]:hidden"
                                  >
                                    {route.badge}
                                  </Badge>
                                )}
                              </span>
                            </SidebarMenuButton>
                          </TooltipTrigger>
                          <TooltipContent>{ALL_BOOKS_DISABLED_TOOLTIP}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <SidebarMenuButton
                        key={route.href + route.label}
                        asChild
                        isActive={isRouteActive(route.href, route.exact)}
                        tooltip={route.label}
                      >
                        <Link href={route.href}>
                          <route.icon
                            strokeWidth={isRouteActive(route.href, route.exact) ? 2 : 1.5}
                            width={16}
                            height={16}
                            className="text-sidebar-icon"
                          />
                          <span>{route.label}</span>
                          {route.badge && (
                            <Badge
                              variant="brand"
                              size="xs"
                              className="ml-auto px-1.5 group-data-[collapsible=icon]:hidden"
                            >
                              {route.badge}
                            </Badge>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    )
                  })}
              </SidebarMenu>
            </SidebarGroup>
          ))}
        </SidebarContent>
        <SidebarFooter className="flex-row items-center">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="ml-auto group-data-[collapsible=icon]:mr-0 group-data-[collapsible=icon]:ml-0 group-data-[collapsible=icon]:size-8!"
          >
            <Sun className="h-[1.2rem] w-[1.2rem] scale-100 rotate-0 transition-all dark:scale-0 dark:-rotate-90" />
            <Moon className="absolute h-[1.2rem] w-[1.2rem] scale-0 rotate-90 transition-all dark:scale-100 dark:rotate-0" />
          </Button>
        </SidebarFooter>
      </SidebarUI>
    </div>
  )
}
